import { useState, useCallback } from "react";
import type { ManagedContentRecord } from "@tellescope/types-models";
import { useEnduserStore } from "../store/hooks";

interface UseContentPreviewOptions {
  onStart?: () => void;
  onSuccess?: (url: string) => void;
  onError?: (error: Error) => void;
}

interface UseContentPreviewReturn {
  /** Load a preview URL for content with attachments (PDF/Video) */
  loadAttachmentUrl: (content: ManagedContentRecord) => Promise<string | null>;
  /** The currently loaded attachment URL */
  attachmentUrl: string | null;
  /** The content currently being previewed */
  previewContent: ManagedContentRecord | null;
  /** Whether a preview is currently loading */
  isLoading: boolean;
  /** Error from the last preview attempt */
  error: Error | null;
  /** Clear the current preview */
  clearPreview: () => void;
}

/**
 * Hook for loading content preview URLs using the Tellescope SDK.
 *
 * For PDF and Video content, uses the file_download_URL API to get a presigned URL.
 * For Article content, the htmlContent is used directly (no URL needed).
 */
export function useContentPreview(options: UseContentPreviewOptions = {}): UseContentPreviewReturn {
  const { onStart, onSuccess, onError } = options;
  const session = useEnduserStore((state) => state.session);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [attachmentUrl, setAttachmentUrl] = useState<string | null>(null);
  const [previewContent, setPreviewContent] = useState<ManagedContentRecord | null>(null);

  const loadAttachmentUrl = useCallback(
    async (content: ManagedContentRecord): Promise<string | null> => {
      if (!session) {
        const err = new Error("No active session");
        setError(err);
        onError?.(err);
        return null;
      }

      // For Articles, no attachment URL is needed - htmlContent is used directly
      if (content.type === "Article") {
        setPreviewContent(content);
        setAttachmentUrl(null);
        return null;
      }

      // For PDF and Video, we need to load the attachment URL
      const attachment = content.attachments?.[0];
      if (!attachment?.secureName) {
        const err = new Error("Content has no attachment to preview");
        setError(err);
        onError?.(err);
        return null;
      }

      setIsLoading(true);
      setError(null);
      onStart?.();

      try {
        // Get the presigned download URL from the API
        const { downloadURL } = await session.api.files.file_download_URL({
          secureName: attachment.secureName,
        });

        setAttachmentUrl(downloadURL);
        setPreviewContent(content);
        onSuccess?.(downloadURL);
        return downloadURL;
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Failed to load content preview");
        setError(error);
        onError?.(error);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [session, onStart, onSuccess, onError]
  );

  const clearPreview = useCallback(() => {
    setAttachmentUrl(null);
    setPreviewContent(null);
    setError(null);
  }, []);

  return {
    loadAttachmentUrl,
    attachmentUrl,
    previewContent,
    isLoading,
    error,
    clearPreview,
  };
}
